// Campaign flow state + persistence.
//
//   BOOT → titleIntro → prologue → mapReveal → mapReady → rinIntro → match(린)
//                        ↑                                 │ win
//                        │              rinOutro ──────────┘
//                        ↓                                 │
//                     bramMeet → bramMatch(브람) → bramOutro
//                                            │ win ↑
//                                            └─────┘
//
// Persisted: `campaignIntroSeen`, `stage1State`, `stage2State`, `stage3State`
// ("available" → "cleared" on each win). A returning player skips the prologue
// and lands on the map, which restores pin states from those flags.

import { key } from "../../gameId.ts";

export type Scene =
  | "titleIntro" // COVER — full-bleed key art, logo, preload spinner → tap to start
  | "prologue" // ACT1_PROLOGUE — opening subtitles
  | "mapReveal" // ACT1_MAP_REVEAL — pins fade in one by one
  | "mapReady" // ACT1_MAP_READY — pins per progress; active one is clickable
  | "rinIntro" // STAGE1_RIN_INTRO — pre-match dialogue
  | "match" // STAGE1_MATCH — the 린 duel
  | "rinOutro" // STAGE1_RIN_OUTRO — 린 joins after a win
  | "bramMeet" // ACT1_BRAM_MEET — meeting 브람, leads into the duel
  | "bramMatch" // STAGE2_MATCH — the 브람 duel
  | "bramOutro" // STAGE2_BRAM_OUTRO — 브람 joins after a win
  | "seraMeet" // ACT1_SERA_MEET — 세라 assigns the 균열 짐승, leads into the match
  | "seraMatch" // STAGE3_MATCH — the 균열 짐승 duel (세라 assists from the side)
  | "seraOutro" // STAGE3_SERA_OUTRO — 세라 joins as scout lead after a win
  | "act1Done" // ACT1_TO_ACT2 — Act II opening subtitle over the 협곡 map
  | "torMeet" // STAGE4_TOR_MEET — meeting 토르, leads into the trail-reading duel
  | "torMatch" // STAGE4_MATCH — the 토르 duel
  | "torOutro" // STAGE4_TOR_OUTRO — 토르 points on to 니아 after a win
  | "niaMeet" // STAGE5_NIA_MEET — meeting 니아, leads into the mask rite duel
  | "niaMatch" // STAGE5_MATCH — the 니아 duel
  | "niaOutro" // STAGE5_NIA_OUTRO — 니아 points on to the 협곡 관문 after a win
  | "calderMeet" // STAGE6_CALDER_MEET — meeting 칼데르, leads into the gate rite
  | "calderMatch" // STAGE6_MATCH — the 칼데르 duel
  | "calderOutro" // STAGE6_CALDER_OUTRO — 칼데르 opens the gate, points to 오르나
  | "act2Done" // ACT2_TO_ACT3 — bridge subtitle over the 달빛 폐허 map
  | "ornaMeet" // STAGE7_ORNA_MEET — the broken-inscription scene, leads into the rite
  | "ornaMatch" // STAGE7_MATCH — the 오르나 duel (seal-plate rite)
  | "ornaOutro" // STAGE7_ORNA_OUTRO — 오르나 names the restore condition, points to 마레크
  | "marekMeet" // STAGE8_MAREK_MEET — the gate-guard who takes a wager for the 인장
  | "marekMatch" // STAGE8_MATCH — the 마레크 duel (the wager)
  | "marekOutro" // STAGE8_MAREK_OUTRO — 마레크 pays out the 청동 인장, points to 이브라
  | "ibraMeet" // STAGE9_IBRA_MEET — 위장 작전: a loud decoy board to pull the scouts
  | "ibraMatch" // STAGE9_MATCH — the 이브라 decoy duel
  | "ibraOutro" // STAGE9_IBRA_OUTRO — retreat secured; 이브라 points to 가시 궁정 / 로웬
  | "rowenMeet" // STAGE10_LOWEN_MEET — 로웬's 공개 재판 at the 가시 궁정 gate
  | "rowenMatch" // STAGE10_MATCH — the 로웬 duel (the judgment)
  | "rowenOutro" // STAGE10_LOWEN_OUTRO — verdict passed; 통행권 + war beyond the gate
  | "act3Done" // ACT3_TO_ACT4 — bridge subtitle over the 가시 궁정 map
  | "drabenMeet" // STAGE11_DRABEN_MEET — 벨라's 선봉장 blocks the entrance
  | "drabenMatch" // STAGE11_MATCH — the 드라벤 duel (player 후공)
  | "drabenOutro" // STAGE11_DRABEN_OUTRO — first 방벽 falls; 드라벤 names 실렌
  | "silenMeet" // STAGE12_SILEN_MEET — the 수정 사서 holds the party's records
  | "silenMatch" // STAGE12_MATCH — the 실렌 duel (player 후공)
  | "silenOutro" // STAGE12_SILEN_OUTRO — records reclaimed; 벨라's 봉인 심장 plan
  | "kairosMeet" // STAGE13_KAIROS_MEET — the 사냥꾼 holds 린 at the 사냥문
  | "kairosMatch" // STAGE13_MATCH — the 카이로스 duel (player 후공)
  | "kairosOutro" // STAGE13_KAIROS_OUTRO — 막 IV done; 심장부 방벽 opens, 아르덴 revealed
  | "act4Done" // ACT4_TO_ACT5 — bridge subtitle over the 에메랄드 심장 map
  | "ardenMeet" // STAGE14_ARDEN_MEET — the chained 파수꾼, corrupted and unfocused
  | "ardenMatch" // STAGE14_MATCH — the 아르덴 duel (해방, player 후공)
  | "ardenOutro" // STAGE14_ARDEN_OUTRO — corruption breaks; 벨라 named, 아르덴 stays
  | "belaMeet" // STAGE15_BELA_MEET — the throne room; 벨라 states her order-by-force thesis
  | "belaMatch" // STAGE15_MATCH — the 벨라 duel (최종 저지, player 후공)
  | "belaOutro" // STAGE15_BELA_OUTRO — campaign finale; 봉인의 심장 stabilises
  | "ending"; // CAMPAIGN_ENDING — 3-cut narration; 등불 마을 relit, bookends the prologue

export type StageState = "locked" | "available" | "cleared";

const K_INTRO_SEEN = key("campaignIntroSeen");
const K_STAGE1 = key("stage1State");
const K_STAGE2 = key("stage2State");
const K_STAGE3 = key("stage3State");
const K_STAGE4 = key("stage4State");
const K_STAGE5 = key("stage5State");
const K_STAGE6 = key("stage6State");
const K_STAGE7 = key("stage7State");
const K_STAGE8 = key("stage8State");
const K_STAGE9 = key("stage9State");
const K_STAGE10 = key("stage10State");
const K_STAGE11 = key("stage11State");
const K_STAGE12 = key("stage12State");
const K_STAGE13 = key("stage13State");
const K_STAGE14 = key("stage14State");
const K_STAGE15 = key("stage15State");

function read(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null; // private mode / storage disabled
  }
}

function write(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore — flow still works for this session */
  }
}

function stageState(k: string): StageState {
  const v = read(k);
  return v === "cleared" || v === "available" ? v : "locked";
}

/** Every fresh mount starts at the title cover; its tap routes on from there
 *  (see hasSeenIntro) — to the prologue for a new save, or straight to the
 *  map for a returning one. */
export function initialScene(): Scene {
  return "titleIntro";
}

/** Whether the prologue has already played — titleIntro's tap skips straight to the map if so. */
export function hasSeenIntro(): boolean {
  return read(K_INTRO_SEEN) === "true";
}

export function getStage1State(): StageState {
  return stageState(K_STAGE1);
}

export function getStage2State(): StageState {
  return stageState(K_STAGE2);
}

export function getStage3State(): StageState {
  return stageState(K_STAGE3);
}

export function getStage4State(): StageState {
  return stageState(K_STAGE4);
}

export function getStage5State(): StageState {
  return stageState(K_STAGE5);
}

export function getStage6State(): StageState {
  return stageState(K_STAGE6);
}

export function getStage7State(): StageState {
  return stageState(K_STAGE7);
}

export function getStage8State(): StageState {
  return stageState(K_STAGE8);
}

export function getStage9State(): StageState {
  return stageState(K_STAGE9);
}

export function getStage10State(): StageState {
  return stageState(K_STAGE10);
}

export function getStage11State(): StageState {
  return stageState(K_STAGE11);
}

export function getStage12State(): StageState {
  return stageState(K_STAGE12);
}

export function getStage13State(): StageState {
  return stageState(K_STAGE13);
}

export function getStage14State(): StageState {
  return stageState(K_STAGE14);
}

export function getStage15State(): StageState {
  return stageState(K_STAGE15);
}

/** Called once the prologue subtitles finish. */
export function markCampaignIntroSeen(): void {
  write(K_INTRO_SEEN, "true");
  write(K_STAGE1, "available");
}

/** Called when the player wins the 린 duel. */
export function markStage1Cleared(): void {
  write(K_STAGE1, "cleared");
}

/** Called when the player wins the 브람 duel. */
export function markStage2Cleared(): void {
  write(K_STAGE2, "cleared");
}

/** Called when the player beats the 균열 짐승. */
export function markStage3Cleared(): void {
  write(K_STAGE3, "cleared");
}

/** Act II wins — 토르, 니아, 칼데르. */
export function markStage4Cleared(): void {
  write(K_STAGE4, "cleared");
}

export function markStage5Cleared(): void {
  write(K_STAGE5, "cleared");
}

export function markStage6Cleared(): void {
  write(K_STAGE6, "cleared");
}

/** Act III wins — 오르나, 마레크, 이브라, 로웬. */
export function markStage7Cleared(): void {
  write(K_STAGE7, "cleared");
}

export function markStage8Cleared(): void {
  write(K_STAGE8, "cleared");
}

export function markStage9Cleared(): void {
  write(K_STAGE9, "cleared");
}

export function markStage10Cleared(): void {
  write(K_STAGE10, "cleared");
}

/** Act IV wins — 드라벤, 실렌, 카이로스. */
export function markStage11Cleared(): void {
  write(K_STAGE11, "cleared");
}

export function markStage12Cleared(): void {
  write(K_STAGE12, "cleared");
}

export function markStage13Cleared(): void {
  write(K_STAGE13, "cleared");
}

/** Act V wins — 아르덴, 벨라. */
export function markStage14Cleared(): void {
  write(K_STAGE14, "cleared");
}

export function markStage15Cleared(): void {
  write(K_STAGE15, "cleared");
}

/** Wipe progress so the flow replays from the prologue (test-build QA aid). */
export function resetCampaign(): void {
  try {
    localStorage.removeItem(K_INTRO_SEEN);
    localStorage.removeItem(K_STAGE1);
    localStorage.removeItem(K_STAGE2);
    localStorage.removeItem(K_STAGE3);
    localStorage.removeItem(K_STAGE4);
    localStorage.removeItem(K_STAGE5);
    localStorage.removeItem(K_STAGE6);
    localStorage.removeItem(K_STAGE7);
    localStorage.removeItem(K_STAGE8);
    localStorage.removeItem(K_STAGE9);
    localStorage.removeItem(K_STAGE10);
    localStorage.removeItem(K_STAGE11);
    localStorage.removeItem(K_STAGE12);
    localStorage.removeItem(K_STAGE13);
    localStorage.removeItem(K_STAGE14);
    localStorage.removeItem(K_STAGE15);
  } catch {
    /* ignore */
  }
}

/** Test aid — the right-panel "다시 시작" jumps here: replay the current 막 from
 *  its opening narration. In 막 I this wipes everything (→ prologue); in 막 II it
 *  keeps 막 I done and only clears 막 II's stages (→ the 막 II intro subtitle).
 *  Returns the scene the flow controller should show. */
export function restartCurrentAct(): Scene {
  // 막 V (stage 13 cleared): keep 막 I–IV, clear only 막 V's stages, replay from
  // the 막 IV→V bridge subtitle.
  if (stageState(K_STAGE13) === "cleared") {
    try {
      localStorage.removeItem(K_STAGE14);
      localStorage.removeItem(K_STAGE15);
    } catch {
      /* ignore */
    }
    return "act4Done";
  }
  // 막 IV (stage 10 cleared): keep 막 I–III, clear only 막 IV's stages, replay
  // from the 막 III→IV bridge subtitle.
  if (stageState(K_STAGE10) === "cleared") {
    try {
      localStorage.removeItem(K_STAGE11);
      localStorage.removeItem(K_STAGE12);
      localStorage.removeItem(K_STAGE13);
    } catch {
      /* ignore */
    }
    return "act3Done";
  }
  // 막 III (stage 6 cleared): keep 막 I·II, clear only 막 III's stages, replay
  // from the 막 II→III bridge subtitle.
  if (stageState(K_STAGE6) === "cleared") {
    try {
      localStorage.removeItem(K_STAGE7);
      localStorage.removeItem(K_STAGE8);
      localStorage.removeItem(K_STAGE9);
      localStorage.removeItem(K_STAGE10);
    } catch {
      /* ignore */
    }
    return "act2Done";
  }
  if (stageState(K_STAGE3) === "cleared") {
    try {
      localStorage.removeItem(K_STAGE4);
      localStorage.removeItem(K_STAGE5);
      localStorage.removeItem(K_STAGE6);
    } catch {
      /* ignore */
    }
    return "act1Done";
  }
  resetCampaign();
  return "prologue";
}
